import React, { useMemo, useState, useEffect } from "react";
import { graphql, navigate } from "gatsby";

// We're using Gutenberg so we need the block styles
// these are copied into this project due to a conflict in the postCSS
// version used by the Gatsby and @wordpress packages that causes build
// failures.
// @todo update this once @wordpress upgrades their postcss version
import "../../css/@wordpress/block-library/build-style/style.css";
import "../../css/@wordpress/block-library/build-style/theme.css";
import Layout from "../../components/layout/layout";
import Seo from "../../components/seo";
import PostCardList from "../../components/postCardList/postCardList";
import "./articleList.scss";
import BreadcrumbHeader from "../../components/common/breadcrumb-header/breadcrumbHeader";
import {
  CARD_ORIENTATION,
  IMG_POSITION,
} from "../../components/common/post-card/postCard.constant";
import CategoriesList from "../../components/common/categories-list/categoriesList";
import {
  ALL_CATEGORY_DATA,
  ARTICLE_LIST_DATA,
  BUZZ_ARTICLES_LIST_CAROUSEL_FLOW,
  BUZZ_ARTICLES_LIST_FLOW,
  BUZZ_CATEGORY_ARTICLES_LIST_PAGINATION_FLOW,
  CATEGORY_ARTICLE_LIST_DATA,
  CATEGORY_FILTERS,
  POPULAR_POST_FLOW,
} from "./articleList.constants";
import PopularPosts from "../../components/popularPosts/popularPosts";
import {
  getArticlesListPagePosts,
  getPostsSplits,
  isDigestUri,
  showPagination,
} from "../../utils/buzzHelper";
import { SEO_META_NO_INDEX } from "../../constants/home";
import Pagination from "../../components/common/pagination/pagination";
import { CAROUSEL_VARIANT } from "../../constants/calculators";
import { IMG_VARIANTS } from "../../constants/imgVariants";
import {
  triggerBuzzClickEvent,
  triggerBuzzPageLoadEvent,
} from "../../utils/garfieldHelpers";
import {
  EVENT_NAMES,
  SECTION_IDS,
  EVENT_SOURCE,
} from "../../constants/garfieldEvents";
import { DAILY_DIGEST_URI , WEEKLY_DIGEST_URI } from "../../components/digestBanner/digestBanner.constant";

const ArticleList = ({ data, pageContext }) => {
  const {
    categories,
    id,
    allPosts,
    pageNumber,
    totalPages,
    postsPerPage,
  } = pageContext;

  const [pageNumberIndex, setPageNumberIndex] = useState(
    pageNumber - 1
  );

  const categoryUri =
    id === ALL_CATEGORY_DATA.id
      ? ALL_CATEGORY_DATA.uri
      : data?.wpCategory?.uri;
  const categoryName =
    id === ALL_CATEGORY_DATA.id
      ? ALL_CATEGORY_DATA.name
      : data?.wpCategory?.name;

  const categoryPosts = data?.allWpPost?.nodes;

  const filterAllPosts = useMemo(() => {
    return allPosts?.filter(( post ) => {
         return ![DAILY_DIGEST_URI,WEEKLY_DIGEST_URI].includes(post?.categories?.nodes?.[0]?.uri);
     })
  },[]);

  const posts = getArticlesListPagePosts({
    categoryId: id,
    allPosts: filterAllPosts,
    categoryPosts,
    currentPage: pageNumberIndex,
    postsPerPage,
  });

  const postsSplit = getPostsSplits(posts);

  const popularPosts = posts?.filter(item =>
      item.tags?.nodes?.some(tag => tag?.name === "popular")
    )
    .map(item => item?.uri);

  const categoriesList = useMemo(() => {
    let articleCategoryList = categories?.reduce(
      (categoryList, curr) => {
        if (
          !curr?.posts?.nodes?.length ||
          isDigestUri(curr?.uri)
        )
          return categoryList;
        return [
          {
            title: curr.name,
            href: curr.uri,
            selected: curr.uri === data?.wpCategory?.uri,
          },
          ...categoryList,
        ];
      },
      []
    );
    articleCategoryList = [
      {
        title: ALL_CATEGORY_DATA.name,
        href: ALL_CATEGORY_DATA.uri,
        selected: id === ALL_CATEGORY_DATA.id,
      },
      ...articleCategoryList,
    ];
    const selectedCategory = articleCategoryList?.find(
      item => item.selected
    );
    articleCategoryList = articleCategoryList?.filter(
      item => !item.selected
    );

    return [selectedCategory, ...articleCategoryList];
  }, [categories]);

  const breadcrumbLinks = [
    {
      uri: "/",
      name: "Buzz",
    },
    {
      uri: "",
      name: categoryName,
    },
  ];

  const handleCategoryClick = uri => {
    navigate(uri);
    triggerBuzzClickEvent(
      EVENT_NAMES.ARTICLE_CATEGORY_CLICKED,
      {
        [ARTICLE_LIST_DATA.CATEGORY]: categoryName,
        [ARTICLE_LIST_DATA.URL]: uri,
      },
      SECTION_IDS.ARTICLE_CATEGORY_CAROUSEL,
      EVENT_SOURCE.CATEGORY_ARTICLE_LIST,
      BUZZ_ARTICLES_LIST_CAROUSEL_FLOW
    );
  };

  const ArticleBanner = () => {
    return (
      <>
        <BreadcrumbHeader
          breadcrumbLinks={breadcrumbLinks}
          title={categoryName}
        />
        <CategoriesList
          categories={categoriesList}
          title={CATEGORY_FILTERS}
          handleCategoryClick={handleCategoryClick}
          variant={CAROUSEL_VARIANT.LIGHT}
        />
      </>
    );
  };

  const handleUpdatePageChange = pageNumberIndex => {
    setPageNumberIndex(pageNumberIndex);
    if (pageNumberIndex === 0) {
      navigate(categoryUri);
    } else {
      navigate(`${categoryUri}${pageNumberIndex + 1}`);
    }
    triggerBuzzClickEvent(
      EVENT_NAMES.CATEGORY_LIST_PAGINATION_CLICKED,
      {
        [ARTICLE_LIST_DATA.CATEGORY]: categoryName,
        [ARTICLE_LIST_DATA.URL]: categoryUri,
        [ARTICLE_LIST_DATA.PAGE_NUMBER]: pageNumberIndex,
      },
      SECTION_IDS.PAGINATION,
      EVENT_SOURCE.CATEGORY_ARTICLE_LIST,
      BUZZ_CATEGORY_ARTICLES_LIST_PAGINATION_FLOW
    );
  };

  useEffect(() => {
    triggerBuzzPageLoadEvent(
      EVENT_NAMES.ARTICLE_LIST_PAGE_LOADED,
      {
        [CATEGORY_ARTICLE_LIST_DATA.CATEGORY_NAME]:
          data?.wpCategory?.name,
        [CATEGORY_ARTICLE_LIST_DATA.CATEGORY_URI]: categoryUri,
        [CATEGORY_ARTICLE_LIST_DATA.PAGE_NUMBER]: pageNumberIndex,
      }
    );
  }, []);

  return (
    <>
      <Layout articleBanner={<ArticleBanner />}>
        <Seo
          title={data?.wpCategory?.name}
          description={data?.wpCategory?.name}
          pageURI={categoryUri}
          meta={SEO_META_NO_INDEX}
        />
        {postsSplit && (
          <div className="article-list">
            {postsSplit[0] && (
              <PostCardList
                posts={postsSplit[0]}
                showMoreVisibility={false}
                showMoreCount={4}
                cardOrientation={CARD_ORIENTATION.VERTICAL}
                customClassName={"vertical-grid"}
                imgVariant={IMG_VARIANTS.ALL_ARTICLES}
                sectionId={
                  SECTION_IDS.CATEGORY_ARTICLE_LIST
                }
                source={EVENT_SOURCE.CATEGORY_ARTICLE_LIST}
                flow={BUZZ_ARTICLES_LIST_FLOW}
              />
            )}

            {postsSplit[1] && (
              <PostCardList
                posts={postsSplit[1]}
                showMoreVisibility={false}
                showMoreCount={4}
                cardOrientation={
                  CARD_ORIENTATION.HORIZONTAL
                }
                customClassName={"horizontal-list"}
                imgPosition={IMG_POSITION.RIGHT}
                imgVariant={IMG_VARIANTS.ALL_ARTICLES}
                sectionId={
                  SECTION_IDS.CATEGORY_ARTICLE_LIST
                }
                source={EVENT_SOURCE.CATEGORY_ARTICLE_LIST}
                flow={BUZZ_ARTICLES_LIST_FLOW}
              />
            )}
            {popularPosts && (
              <PopularPosts
                popularPosts={popularPosts}
                postData={posts}
                sectionId={SECTION_IDS.POPULAR_POSTS}
                source={EVENT_SOURCE.CATEGORY_ARTICLE_LIST}
                flow={POPULAR_POST_FLOW}
              />
            )}

            {postsSplit[2] && (
              <PostCardList
                posts={postsSplit[2]}
                showMoreVisibility={false}
                showMoreCount={4}
                cardOrientation={
                  CARD_ORIENTATION.HORIZONTAL
                }
                customClassName={"horizontal-list"}
                imgPosition={IMG_POSITION.RIGHT}
                imgVariant={IMG_VARIANTS.ALL_ARTICLES}
                sectionId={
                  SECTION_IDS.CATEGORY_ARTICLE_LIST
                }
                source={EVENT_SOURCE.CATEGORY_ARTICLE_LIST}
                flow={BUZZ_ARTICLES_LIST_FLOW}
              />
            )}
            {showPagination(posts, totalPages) && (
              <Pagination
                pageNumber={pageNumberIndex}
                totalPages={totalPages}
                updatePageNumber={handleUpdatePageChange}
              />
            )}
          </div>
        )}
      </Layout>
    </>
  );
};

export default ArticleList;

export const pageQuery = graphql`
  query WPIndividualCategoryPosts(
    $id: String!
    $offset: Int!
    $postsPerPage: Int!
  ) {
    allWpPost(
      filter: {
        categories: {
          nodes: { elemMatch: { id: { eq: $id } } }
        }
      }
      sort: { fields: [date], order: DESC }
      limit: $postsPerPage
      skip: $offset
    ) {
      nodes {
        excerpt
        uri
        date(formatString: "MMMM DD, YYYY")
        title
        excerpt
        slug
        author {
          node {
            id
            name
          }
        }
        seo {
          readingTime
        }
        categories {
          nodes {
            id
            name
            uri
            slug
          }
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid_tracedSVG
                }
              }
            }
          }
        }
        tags {
          nodes {
            id
            name
            uri
          }
        }
      }
    }
    categoriesData: allWpCategory {
      nodes {
        id
        name
        count
        uri
      }
    }
    recentPosts: allWpPost(
      sort: { order: DESC, fields: date }
      limit: 5
    ) {
      nodes {
        id
        title
        uri
      }
    }
    wpCategory(id: { eq: $id }) {
      name
      uri
    }
  }
`;
